/**
 * LoginPage — Email/password sign-in with Google & Microsoft OAuth.
 *
 * Uses Sinvoice design primitives (Input, Button).
 * Redirects to /dashboard on success.
 */
import { useState, useEffect, type FormEvent } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useAuthStore } from "@/stores/useAuthStore";
import { Input } from "@/components/ui/Input";
import { Button } from "@/components/ui/Button";

export function LoginPage() {
  const navigate = useNavigate();
  const { login, loginWithGoogle, loginWithMicrosoft, isLoading, isAuthenticated, error } =
    useAuthStore();

  // Redirect if already authenticated
  useEffect(() => {
    if (isAuthenticated) {
      navigate("/dashboard", { replace: true });
    }
  }, [isAuthenticated, navigate]);

  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");

  async function handleSubmit(e: FormEvent) {
    e.preventDefault();
    const ok = await login(email, password);
    if (ok) navigate("/dashboard");
  }

  return (
    <div className="flex min-h-screen items-center justify-center bg-surface">
      <div className="w-full max-w-sm rounded-xl border border-white/10 bg-surface-raised p-8 shadow-card">
        {/* Logo */}
        <h1 className="mb-2 text-center text-2xl font-bold tracking-tight text-primary">
          AJNT
        </h1>
        <p className="mb-6 text-center text-sm text-muted-foreground">
          Sign in to your account
        </p>

        {/* OAuth */}
        <div className="mb-4 flex flex-col gap-2">
          <Button
            variant="secondary"
            className="w-full"
            onClick={() => loginWithGoogle()}
          >
            Sign in with Google
          </Button>
          <Button
            variant="secondary"
            className="w-full"
            onClick={() => loginWithMicrosoft()}
          >
            Sign in with Microsoft
          </Button>
        </div>

        {/* Divider */}
        <div className="mb-4 flex items-center gap-3">
          <div className="h-px flex-1 bg-white/10" />
          <span className="text-xs text-muted-foreground">or</span>
          <div className="h-px flex-1 bg-white/10" />
        </div>

        {/* Email/password form */}
        <form onSubmit={handleSubmit} className="flex flex-col gap-4">
          <Input
            label="Email"
            type="email"
            placeholder="you@example.com"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            required
          />
          <Input
            label="Password"
            type="password"
            placeholder="••••••••"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            required
          />

          {error && (
            <p className="text-sm text-danger" role="alert">
              {error}
            </p>
          )}

          <Button type="submit" className="w-full" loading={isLoading}>
            Sign In
          </Button>
        </form>

        <p className="mt-4 text-center text-sm text-muted-foreground">
          Don&apos;t have an account?{" "}
          <Link
            to="/signup"
            className="font-medium text-primary hover:underline"
          >
            Sign up
          </Link>
        </p>
      </div>
    </div>
  );
}
